import { useEffect, useState } from "react";
import type { EqmsController } from "../../../controllers/useEqms";
import * as eqmsApi from "../../../controllers/eqmsApi";
import { friendlyMessage } from "../../../controllers/eqmsApi";
import type { DocReviewTask, DocStatus, Document, Version, VersionStatus } from "../../../models/eqms";
import { DOC_STATUS_LABEL, VERSION_STATUS_LABEL } from "../../../models/eqms";

// The eQMS module's landing page: a personal roll-up across the project — pending reviews assigned
// to me, document status breakdown, and the version roadmap. Each widget loads independently and
// shows its own loading/empty state so one slow/failing call doesn't block the others.
export function EqmsDashboardView({ eqms }: { eqms: EqmsController }) {
  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>대시보드</h2>
        <div className="note" style={{ marginTop: 4 }}>프로젝트 전체 현황 요약</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16 }}>
        <MyReviewInboxWidget eqms={eqms} />
        <DocumentStatusWidget eqms={eqms} />
        <VersionRoadmapWidget eqms={eqms} />
      </div>
    </div>
  );
}

function MyReviewInboxWidget({ eqms }: { eqms: EqmsController }) {
  const [tasks, setTasks] = useState<DocReviewTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    eqmsApi.myReviewInbox(eqms.getToken)
      .then((list) => { if (alive) setTasks(list.filter((t) => t.action === "PENDING")); })
      .catch((e) => { if (alive) setError(friendlyMessage(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [eqms.getToken]);

  return (
    <div className="info-card">
      <h4>내 검토 대기</h4>
      {loading ? (
        <div className="center">불러오는 중…</div>
      ) : error ? (
        <div className="note" style={{ background: "#fdeef0" }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      ) : tasks.length === 0 ? (
        <div className="sdui-list-empty">대기 중인 검토가 없습니다.</div>
      ) : (
        <>
          <div className="tag" style={{ marginBottom: 8 }}>{tasks.length}건</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {tasks.slice(0, 5).map((t) => (
              <button
                key={t.id}
                className="btn"
                style={{ textAlign: "left" }}
                onClick={() => eqms.openDetail("documents", t.documentId)}
              >
                문서 {t.documentId.slice(0, 8)} · {t.role === "REVIEWER" ? "검토자" : "승인자"}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function DocumentStatusWidget({ eqms }: { eqms: EqmsController }) {
  const [docs, setDocs] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eqms.projectId) { setDocs([]); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    eqmsApi.listDocuments(eqms.getToken, eqms.projectId)
      .then((list) => { if (alive) setDocs(list); })
      .catch((e) => { if (alive) setError(friendlyMessage(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [eqms.getToken, eqms.projectId]);

  const counts: Record<DocStatus, number> = {
    DRAFT: 0, IN_REVIEW: 0, APPROVED: 0, RELEASED: 0, OBSOLETE: 0,
  };
  for (const d of docs) counts[d.status]++;

  return (
    <div className="info-card">
      <h4>문서 현황</h4>
      {!eqms.projectId ? (
        <div className="sdui-list-empty">프로젝트를 선택하세요.</div>
      ) : loading ? (
        <div className="center">불러오는 중…</div>
      ) : error ? (
        <div className="note" style={{ background: "#fdeef0" }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      ) : docs.length === 0 ? (
        <div className="sdui-list-empty">문서가 없습니다.</div>
      ) : (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {(Object.keys(DOC_STATUS_LABEL) as DocStatus[]).map((s) => (
            <span key={s} className="tag">{DOC_STATUS_LABEL[s]} {counts[s]}</span>
          ))}
        </div>
      )}
    </div>
  );
}

function VersionRoadmapWidget({ eqms }: { eqms: EqmsController }) {
  const [versions, setVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!eqms.projectId) { setVersions([]); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    eqmsApi.listVersions(eqms.getToken, eqms.projectId)
      .then((list) => { if (alive) setVersions(list); })
      .catch((e) => { if (alive) setError(friendlyMessage(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [eqms.getToken, eqms.projectId]);

  const counts: Record<VersionStatus, number> = { PLANNED: 0, IN_DEV: 0, RELEASED: 0, SUPERSEDED: 0 };
  for (const v of versions) counts[v.status]++;
  const nextPlanned = versions
    .filter((v) => v.status === "PLANNED" && v.plannedDate)
    .sort((a, b) => (a.plannedDate! < b.plannedDate! ? -1 : 1))[0];

  return (
    <div className="info-card">
      <h4>버전 로드맵</h4>
      {!eqms.projectId ? (
        <div className="sdui-list-empty">프로젝트를 선택하세요.</div>
      ) : loading ? (
        <div className="center">불러오는 중…</div>
      ) : error ? (
        <div className="note" style={{ background: "#fdeef0" }}>
          <b style={{ color: "var(--amber)" }}>오류:</b> {error}
        </div>
      ) : versions.length === 0 ? (
        <div className="sdui-list-empty">버전이 없습니다.</div>
      ) : (
        <>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            <span className="tag">{VERSION_STATUS_LABEL.PLANNED} {counts.PLANNED}</span>
            <span className="tag">{VERSION_STATUS_LABEL.IN_DEV} {counts.IN_DEV}</span>
            <span className="tag">{VERSION_STATUS_LABEL.RELEASED} {counts.RELEASED}</span>
          </div>
          {nextPlanned ? (
            <div className="note">다음 계획: <b>{nextPlanned.label}</b> ({nextPlanned.plannedDate})</div>
          ) : (
            <div className="note">예정된 계획 버전이 없습니다.</div>
          )}
        </>
      )}
    </div>
  );
}
